import {
    WORKLINE_FILTERS,
    WORKLINE_SORT_OPTIONS,
} from "../Constants/worklineFilter.js";
import { WORKLINE_STATUS_COUNT_VIEWS } from "../Constants/worklineStatusCountView.js";

/**
 * @param {string | null | undefined} selectedFilter
 * @returns {boolean}
 */
export function isWorklineEveryoneFilter(selectedFilter) {
    return selectedFilter === WORKLINE_FILTERS.EVERYONE.value;
}

/**
 * @param {string | null | undefined} selectedFilter
 * @returns {typeof WORKLINE_STATUS_COUNT_VIEWS[keyof typeof WORKLINE_STATUS_COUNT_VIEWS]}
 */
export function resolveWorklineStatusCountViewForFilter(selectedFilter) {
    if (selectedFilter === WORKLINE_FILTERS.REPORTS_TO_ME.value) {
        return WORKLINE_STATUS_COUNT_VIEWS.TEAM;
    }

    if (isWorklineEveryoneFilter(selectedFilter)) {
        return WORKLINE_STATUS_COUNT_VIEWS.ORGANIZATION;
    }

    return WORKLINE_STATUS_COUNT_VIEWS.FILTER;
}

/**
 * @param {Record<string, unknown> | null | undefined} shiftActivity
 * @param {string} view
 * @returns {{ shifted_in_count: number, total_members_count: number } | null}
 */
export function resolveWorklineStatusCountForView(shiftActivity, view) {
    if (shiftActivity == null || typeof shiftActivity !== "object") {
        return null;
    }

    const nested = shiftActivity[view];
    if (nested != null && typeof nested === "object") {
        return {
            shifted_in_count: Number(nested.shifted_in_count ?? 0),
            total_members_count: Number(nested.total_members_count ?? 0),
        };
    }

    const hasLegacyCounts =
        "shifted_in_count" in shiftActivity ||
        "total_members_count" in shiftActivity;

    if (!hasLegacyCounts) {
        return null;
    }

    const legacyCounts = {
        shifted_in_count: Number(shiftActivity.shifted_in_count ?? 0),
        total_members_count: Number(
            shiftActivity.total_members_count ?? 0,
        ),
    };

    if (
        view === WORKLINE_STATUS_COUNT_VIEWS.FILTER ||
        view === WORKLINE_STATUS_COUNT_VIEWS.ORGANIZATION
    ) {
        return legacyCounts;
    }

    return null;
}
const WORKLINE_STATUS_COUNT_STRIP_TOOLTIPS = {
    [WORKLINE_STATUS_COUNT_VIEWS.ORGANIZATION]:
        "This is the count of everyone in your organization, and amongst those, the count of those who are shifted in presently.\n\n" +
        "Clicking here, will allow you to switch between three counts - the current count of your full organization, the count of your filtered Workline, and the count of your team.",

    [WORKLINE_STATUS_COUNT_VIEWS.FILTER]:
        "This is the count of everyone who can show up in this Workline, with your currently applied filters, and amongst those, the count of those who are shifted in presently.\n\n" +
        "Clicking here, will allow you to switch between three counts - the current count of your filtered Workline, the count of your team and the count of your full organization.",

    [WORKLINE_STATUS_COUNT_VIEWS.TEAM]:
        "This is the count of everyone on your team (those who directly report to you, and those who report to them), and amongst those, the count of those who are shifted in presently.\n\n" +
        "Clicking here, will allow you to switch between three counts - the current count of your team, the count of your full organization and the count of your filtered Workline.",
};

/**
 * @param {string} view
 * @returns {string}
 */
export function worklineStatusCountStripTooltipForView(view) {
    return (
        WORKLINE_STATUS_COUNT_STRIP_TOOLTIPS[view] ??
        WORKLINE_STATUS_COUNT_STRIP_TOOLTIPS[
            WORKLINE_STATUS_COUNT_VIEWS.ORGANIZATION
        ]
    );
}

/**
 * @param {string | null | undefined} selectedFilter
 * @returns {string}
 */
export function worklineStatusCountStripTooltip(selectedFilter) {
    const view = resolveWorklineStatusCountViewForFilter(selectedFilter);

    return worklineStatusCountStripTooltipForView(view);
}

/**
 * Next strip view when cycling icons/counts:
 * Organization (pyramid) → Filter → Team → Organization.
 *
 * @param {string} currentView
 * @returns {typeof WORKLINE_STATUS_COUNT_VIEWS[keyof typeof WORKLINE_STATUS_COUNT_VIEWS]}
 */
export function nextWorklineStatusCountView(currentView) {
    switch (currentView) {
        case WORKLINE_STATUS_COUNT_VIEWS.ORGANIZATION:
            return WORKLINE_STATUS_COUNT_VIEWS.FILTER;
        case WORKLINE_STATUS_COUNT_VIEWS.FILTER:
            return WORKLINE_STATUS_COUNT_VIEWS.TEAM;
        case WORKLINE_STATUS_COUNT_VIEWS.TEAM:
            return WORKLINE_STATUS_COUNT_VIEWS.ORGANIZATION;
        default:
            return WORKLINE_STATUS_COUNT_VIEWS.ORGANIZATION;
    }
}

/**
 * Workline filter.selection to persist when the strip shows a given count view.
 *
 * @param {string} view
 * @returns {string}
 */
export function worklineFilterSelectionForStatusCountView(view) {
    switch (view) {
        case WORKLINE_STATUS_COUNT_VIEWS.ORGANIZATION:
            return WORKLINE_FILTERS.EVERYONE.value;
        case WORKLINE_STATUS_COUNT_VIEWS.FILTER:
            return WORKLINE_FILTERS.DIVISIONS.value;
        case WORKLINE_STATUS_COUNT_VIEWS.TEAM:
            return WORKLINE_FILTERS.REPORTS_TO_ME.value;
        default:
            return WORKLINE_FILTERS.EVERYONE.value;
    }
}

/**
 * @param {string | null | undefined} selectedFilter
 * @returns {typeof WORKLINE_STATUS_COUNT_VIEWS[keyof typeof WORKLINE_STATUS_COUNT_VIEWS]}
 */
export function worklineStatusCountViewFromFilterSelection(selectedFilter) {
    return resolveWorklineStatusCountViewForFilter(selectedFilter);
}

/**
 * @param {string | null | undefined} storedView
 * @param {string | null | undefined} selectedFilter
 * @returns {typeof WORKLINE_STATUS_COUNT_VIEWS[keyof typeof WORKLINE_STATUS_COUNT_VIEWS]}
 */
export function resolveWorklineStatusCountViewPreference(
    storedView,
    selectedFilter,
) {
    if (
        storedView === WORKLINE_STATUS_COUNT_VIEWS.ORGANIZATION ||
        storedView === WORKLINE_STATUS_COUNT_VIEWS.FILTER ||
        storedView === WORKLINE_STATUS_COUNT_VIEWS.TEAM
    ) {
        return storedView;
    }

    return resolveWorklineStatusCountViewForFilter(selectedFilter);
}

/**
 * Next filter.selection when cycling the shift-activity strip (derived from view cycle).
 *
 * @param {string | null | undefined} selectedFilter
 * @returns {string}
 */
export function nextWorklineFilterSelectionForStatusCountStripCycle(
    selectedFilter,
) {
    const currentView = resolveWorklineStatusCountViewForFilter(selectedFilter);
    const nextView = nextWorklineStatusCountView(currentView);

    return worklineFilterSelectionForStatusCountView(nextView);
}

/**
 * @param {{
 *     selectedFilter: string,
 *     selectedSort?: string,
 *     selectedGroup?: string,
 *     selectedDivisions?: number[],
 *     expanded_division_ids?: number[],
 * }} worklineFilterState
 * @param {{ filterSelection?: string }} [options]
 * @returns {{
 *     filter: { selection: string, division_ids: number[] },
 *     sort: string,
 *     group_by_division: boolean,
 *     expanded_division_ids: number[],
 * }}
 */
export function buildWorklinePreferencesPatchPayload(
    worklineFilterState,
    options = {},
) {
    const filterSelection =
        options.filterSelection ?? worklineFilterState.selectedFilter;
    const isDivisionsFilter =
        filterSelection === WORKLINE_FILTERS.DIVISIONS.value;

    const division_ids = isDivisionsFilter
        ? [...(worklineFilterState.selectedDivisions ?? [])]
        : [];

    const group_by_division =
        isDivisionsFilter && Boolean(worklineFilterState.selectedGroup);

    const expanded_division_ids =
        group_by_division && isDivisionsFilter
            ? [...(worklineFilterState.expanded_division_ids ?? [])]
            : [];

    return {
        filter: {
            selection: filterSelection,
            division_ids,
        },
        sort:
            worklineFilterState.selectedSort ??
            WORKLINE_SORT_OPTIONS[0].value,
        group_by_division,
        expanded_division_ids,
    };
}
